import React from 'react';
import styled, { css } from 'styled-components';
import Loader from './Loader';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text';
export type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  children: React.ReactNode;
  as?: React.ElementType;
  to?: string;
}

const ButtonContainer = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$')
})<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $fullWidth: boolean;
  $iconPosition: 'left' | 'right';
  disabled?: boolean;
  as?: React.ElementType;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: var(--font-family-main);
  font-weight: 500;
  border-radius: 9999px;
  transition: all 0.2s ease;
  width: ${props => (props.$fullWidth ? '100%' : 'auto')};
  
  /* Размеры */
  ${props => {
    switch (props.$size) {
      case 'small':
        return css`
          padding: 6px 16px;
          font-size: 14px;
        `;
      case 'large':
        return css`
          padding: 14px 24px;
          font-size: 18px;
        `;
      default:
        return css`
          padding: 12px 20px;
          font-size: 16px;
        `;
    }
  }}
  
  /* Варианты */
  ${props => {
    switch (props.$variant) {
      case 'primary':
        return css`
          background-color: var(--color-primary);
          color: var(--color-text);
          &:hover:not(:disabled) {
            filter: brightness(1.1);
          }
          &:active:not(:disabled) {
            filter: brightness(0.9);
          }
        `;
      case 'secondary':
        return css`
          background-color: var(--color-surface);
          color: var(--color-text);
          &:hover:not(:disabled) {
            filter: brightness(1.1);
          }
          &:active:not(:disabled) {
            filter: brightness(0.9);
          }
        `;
      case 'outline':
        return css`
          border: 1px solid rgba(255, 255, 255, 0.24);
          color: var(--color-text);
          &:hover:not(:disabled) {
            background-color: rgba(255, 255, 255, 0.04);
          }
          &:active:not(:disabled) {
            background-color: rgba(255, 255, 255, 0.08);
          }
        `;
      case 'text':
        return css`
          color: var(--color-text);
          padding-left: 4px;
          padding-right: 4px;
          &:hover:not(:disabled) {
            color: var(--color-primary);
          }
        `;
      default:
        return '';
    }
  }}
  
  /* Порядок иконки */
  ${props => props.$iconPosition === 'right' && css`
    flex-direction: row-reverse;
  `}
  
  /* Отключено */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = 'left',
  children,
  disabled,
  as,
  to,
  ...rest
}) => {
  return (
    <ButtonContainer
      $variant={variant}
      $size={size}
      $fullWidth={fullWidth}
      $iconPosition={iconPosition}
      disabled={loading || disabled}
      as={as}
      to={to}
      {...rest}
    >
      {loading ? (
        <span>Загружается...</span>
      ) : (
        <>
          {icon && icon}
          <span>{children}</span>
        </>
      )}
    </ButtonContainer>
  );
};

export default Button; 